<script setup lang="ts">
import { computed, watch, ref } from 'vue';
import i18n from './i18n';
import { useRouter, useRoute } from 'vue-router';
import routerKey from '@/router/routerKey';
import axios from '@/config/axiosConfig';
import { USER_GROUP_B2B, USER_GROUP_B2B_ADMIN, USER_GROUP_B2B_AGENT } from '@/constants';
import { useIndexStore } from '@/store/IndexStore';
import { getEnvVar } from '@/utils/env';
import { useUserInfo } from '@/modules/user/useUserInfo';
import { GQT_ALL } from '@/role';
import { useToast } from 'primevue/usetoast';
import ConfirmDialog from 'primevue/confirmdialog';
import DynamicDialog from 'primevue/dynamicdialog';
import Toast from 'primevue/toast';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const maintenanceAllowedRoles = getEnvVar('VITE_MAINTENANCE_ALLOWED_ROLES') as Array<string>;
const maintenanceAllowedUsers = getEnvVar('VITE_MAINTENANCE_ALLOWED_USER_EMAIL') as Array<string>;

const router = useRouter();
const route = useRoute();
const toast = useToast();
const indexStore = useIndexStore();
const userInfo = useUserInfo();

const initApp = ref(false);
const newNotification = computed(() => indexStore.newNotification);
const successNotification = computed(() => indexStore.successNotification);
const globalLoading = computed(() => indexStore.globalLoading);
const currentRoute = computed(() => route.name);

function validateAndRoute(jwtToken: string, routerName: string) {
  userInfo.setUserInfo(jwtToken);
  const currentUser = userInfo.userInfoFactory(jwtToken);
  const userGroups = currentUser?.groups;
  const userRole = currentUser?.role;
  const validGroups = [USER_GROUP_B2B, USER_GROUP_B2B_ADMIN, USER_GROUP_B2B_AGENT];
  const allowedRoles = maintenanceAllowedRoles?.length > 0 ? maintenanceAllowedRoles : GQT_ALL;

  const isGroupAllowed = userGroups?.some((group) => validGroups.includes(group));

  if (isGroupAllowed || (userRole !== undefined && allowedRoles.includes(userRole))) {
    if (maintenanceAllowedRoles?.length > 0 && userRole !== undefined && !allowedRoles.includes(userRole)) {
      if (undefined === maintenanceAllowedUsers || !maintenanceAllowedUsers.includes(currentUser?.email)) {
        router.push({ name: routerKey.MAINTENANCE_PAGE });
        return;
      }
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
    if (routerName) {
      router.push({ name: routerName });
    }
    return;
  }
  window.localStorage.clear();
  initApp.value = true;
  router.push({ name: routerKey.LANDING_PAGE });
}

const getUserToken = async (routerName: string) => {
  indexStore.updateLoading(true);

  return await userInfo
    .getToken()
    .then((response: string) => {
      validateAndRoute(response, routerName);
    })
    .catch(() => {
      window.localStorage.clear();
      userInfo.resetUserInfo();
      router.push({ name: routerKey.LANDING_PAGE });
    })
    .finally(() => {
      indexStore.updateLoading(false);
      initApp.value = true;
    });
};

watch(newNotification, (newValue) => {
  if (newValue.response?.status === 406) {
    return;
  }
  toast.add({ severity: 'error', summary: '', detail: newValue.message || i18n.global.t('notification.unexpected_error'), life: 5000 });
  if (newValue.response?.status === 401) {
    window.localStorage.clear();
    userInfo.resetUserInfo();
    router.push({ name: routerKey.LANDING_PAGE });
  }
});

watch(successNotification, (newValue) => {
  toast.add({ severity: 'success', summary: '', detail: newValue.message || i18n.global.t('notification.save_successfully'), life: 3000 });
});

watch(currentRoute, () => {
  if (route.meta.requiresAuth) {
    getUserToken('');
  }
});

getUserToken(routerKey.HOME);
</script>

<template>
  <LoadingSpinner v-if="globalLoading" />
  <div class="gqt-spa-page-container">
    <router-view v-if="initApp" />
  </div>
  <DynamicDialog />
  <Toast />
  <ConfirmDialog></ConfirmDialog>
</template>

<style lang="scss">
@use '@/assets/scss/themes/acv-default/gqt-theme.scss' as *;
@use '@/assets/common.scss' as *;
@use 'primeicons/primeicons.css' as *;
@use '@/assets/styles.scss' as *;

// The parent of the main body needs to have display: flex to display the background properly.
.main-body {
  display: flex;
  flex-direction: column;
  // .plp-template is the first container inside .main-body that has the possibility to fully stretch to fill the space beetween the header and footer.
  // So we add the bg here, then flex: 1 makes it stretch to take the entire height!
  > .groupQuote-plp {
    flex: 1;
    display: flex;
    > .groupQuote-spa {
      display: flex;
      flex-direction: column;
      flex: auto;
    }
  }
}

#gqt-spa {
  height: 100%;
  // In AgentHub, have an additional div...
  > div:first-child {
    height: inherit;
  }
  #vacv-spa-container {
    height: inherit;
    > .vuejsDevWrapper {
      height: inherit;
    }
  }
}

#app {
  height: inherit;
  > .gqt-spa-page-container {
    height: inherit;
  }
}

.p-toast-message-content {
  svg.p-toast-message-icon {
    display: none;
  }
}

.heading-font {
  font-family: 'SharpGroteskSemibold19', Arial, Helvetica, sans-serif;
}
</style>
